import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GitCompareArrows, X } from "lucide-react";
import api from "@/lib/api";
import {
    getCompareIds,
    removeFromCompare,
    clearCompare,
    compareUrl,
    MAX_COMPARE,
} from "@/lib/compare";

/** Sticky shortlist bar — browse page only. Compare happens on /compare. */
const CompareTray = () => {
    const navigate = useNavigate();
    const [ids, setIds] = useState(getCompareIds);
    const [bikes, setBikes] = useState([]);

    useEffect(() => {
        const sync = () => setIds(getCompareIds());
        window.addEventListener("compare-updated", sync);
        return () => window.removeEventListener("compare-updated", sync);
    }, []);

    useEffect(() => {
        if (ids.length === 0) {
            setBikes([]);
            return;
        }
        Promise.all(ids.map((id) => api.get(`/bikes/${id}`).then((r) => r.data).catch(() => null)))
            .then((list) => setBikes(list.filter(Boolean)));
    }, [ids]);

    if (ids.length === 0) return null;

    return (
        <div
            className="fixed bottom-0 inset-x-0 z-40 border-t border-zinc-200 bg-white/95 backdrop-blur-md shadow-[0_-8px_30px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom)]"
            data-testid="compare-tray"
        >
            <div className="container-x py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <div className="flex items-center gap-2 shrink-0">
                    <GitCompareArrows size={18} className="text-[#E31837]" />
                    <span className="text-sm font-semibold text-[#0A2540]">
                        Shortlist ({ids.length}/{MAX_COMPARE})
                    </span>
                </div>
                <div className="hidden sm:flex flex-wrap gap-2 flex-1 min-w-0">
                    {bikes.map((b) => (
                        <span
                            key={b.id}
                            className="inline-flex items-center gap-1.5 bg-[#F4F6F8] border border-zinc-200 rounded-full pl-3 pr-1 py-1 text-xs font-medium text-[#0A2540] max-w-[180px]"
                        >
                            <span className="truncate">{b.name}</span>
                            <button
                                type="button"
                                onClick={() => removeFromCompare(b.id)}
                                className="min-w-[44px] min-h-[44px] w-8 h-8 rounded-full hover:bg-zinc-200 flex items-center justify-center shrink-0"
                                aria-label={`Remove ${b.name}`}
                            >
                                <X size={12} />
                            </button>
                        </span>
                    ))}
                    {ids.length < MAX_COMPARE && (
                        <span className="text-xs text-zinc-500 self-center">
                            Tap <GitCompareArrows className="inline w-3 h-3" /> on more bikes to add
                        </span>
                    )}
                </div>
                <p className="sm:hidden text-xs text-zinc-500 truncate">
                    {bikes.map((b) => b.name).join(" · ")}
                </p>
                <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
                    <button type="button" onClick={() => clearCompare()} className="text-xs text-zinc-500 hover:text-[#E31837] px-3 min-h-[44px]">
                        Clear
                    </button>
                    <button
                        type="button"
                        disabled={ids.length < 2}
                        onClick={() => navigate(compareUrl(ids))}
                        className="btn btn-primary btn-sm disabled:opacity-40 min-h-[44px] flex-1 sm:flex-none"
                        data-testid="compare-tray-go"
                    >
                        Compare side by side
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CompareTray;
